/**
 * Keeps personal data out of the URLs Application Insights sends (CC-17).
 *
 * <b>Why this exists.</b> The standing telemetry rule on M1-31/32/33 is
 * "identifiers and a persona, never a customer's name, email or address". Two
 * SDK collectors break it on their own, without any code of ours asking them
 * to:
 *
 *  - `enableAutoRouteTracking` sends the page URI on every SPA route change,
 *    query string included — and `/customers?q=` carries whatever was typed
 *    into M1-03's search box, which matches name, phone, email and street
 *    address.
 *  - `fetch` and `XHR` are auto-instrumented (`disableFetchTracking` and
 *    `disableAjaxTracking` both default to FALSE), and the dependency item
 *    carries the absolute request URL in `target`, `name` and `data`. So
 *    `GET /api/v1/customers?q=Okafor` is sent whether or not the term was ever
 *    in the address bar.
 *
 * That second one is the reason the staff console's approach — keeping the
 * term out of the URL (M1-33) — is necessary but not sufficient, and the
 * reason this is a redaction problem rather than a routing one.
 *
 * <b>Why an allowlist and not a denylist.</b> The SDK has its own redaction
 * (`redactUrls`/`redactQueryParams`), but it matches param names against a
 * list of known-sensitive ones — `sig`, `Signature`, `AWSAccessKeyId`,
 * `X-Goog-Signature` by default. That fails open: the next param someone adds
 * leaks until a person remembers to add its name to the list, and nothing
 * fails if they don't. This inverts it. A param is kept because it is on
 * SAFE_QUERY_PARAMS; everything else has its VALUE replaced, the name left
 * alone so the shape of the request is still legible in a trace.
 *
 * <b>What is deliberately NOT redacted.</b> Path segments. `/customers/{id}`
 * and `/jobs/{id}` are identifiers, which the rule permits by name, and they
 * are what makes a trace joinable to a record. Only the query string moves —
 * with ONE exception, below.
 *
 * <b>The customer's link is not an identifier, it is a key (M2-07).</b>
 * `/e/{account}.{secret}` opens the estimate with no sign-in, and the API
 * stores the secret hashed and encrypted precisely so a database read alone
 * never yields a working link. The page view (`uri`, and `ext.trace.name`,
 * which the SDK takes from the pathname for EVERY item on the page) and the
 * public read's dependency would each have carried one, so the token segment
 * is replaced wherever those fields are sent (found in review).
 *
 * <b>So is the invitation link (M2-20).</b> `/invite/{account}.{secret}` and
 * `/api/v1/public/invites/{token}` carry the same kind of key: whoever holds
 * one can create the invitee's sign-in and read which company invited them.
 * The invite page also moves the token out of the address bar on arrival
 * (`auth/pendingInvite.ts`), so only the very first page view ever carries
 * it — and this replaces it there too.
 *
 * <b>And the invoice link (M2-16).</b> `/i/{account}.{secret}` and
 * `/api/v1/public/invoices/{token}` open the customer's invoice — amounts,
 * name and service address — with no sign-in; the API redacts the same
 * prefix on its side (`LinkTokenRedaction`).
 *
 * <b>And the appointment-email link (M2-21).</b> `/u/{account}.{secret}` and
 * `/api/v1/public/appointment-emails/{token}` let whoever holds one turn a
 * customer's or occupant's appointment emails off, and show the shop and the
 * masked address; the API redacts the same prefix.
 */
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

/**
 * Query parameters whose values may be sent as-is.
 *
 * Every one is a filter, a page number, a sort order, a date, or an id — the
 * things you need to make sense of a slow or failing request. Nothing here is
 * free text a person typed.
 *
 * `q` is absent, which is the whole point: it is the customer search term in
 * `web` and the tenant search term in `web-staff`.
 *
 * `jobType` is also absent, and less obviously so. It looks like an enum but
 * the job list renders it as a free-text box (`Jobs.tsx`), so it is a field a
 * person types into — and a field a person types into is exactly the class of
 * thing this list must not vouch for. The cost is that a job-type filter shows
 * as redacted in a trace.
 */
const SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  // Dispatch board (M1-13), in the address bar.
  'date',
  'view',
  'tech',
  // Job list filters (M1-06/M1-15), in the address bar.
  'status',
  'unassigned',
  'from',
  'to',
  'includeUnscheduled',
  'customer',
  'sort',
  'dir',
  'page',
  'pageSize',
  // The same filters as the API receives them, which is what the dependency
  // items carry. Named differently from their address-bar counterparts on
  // purpose — these are the API's names, not ours to change here.
  'assignedUserId',
  'unassignedOnly',
  'scheduledFrom',
  'scheduledTo',
  'customerId',
  'sortBy',
  'sortDirection',
  'includeArchived',
  'propertyId',
  'entity',
]);

/** What a redacted value is replaced with. Matches the SDK's own wording. */
const REDACTED = 'REDACTED';

/**
 * A link token, as a path segment: the customer's estimate page (`/e/{token}`)
 * and its API read (`/api/v1/public/estimates/{token}`), and the invitation
 * page (`/invite/{token}`) and its API (`/api/v1/public/invites/{token}`,
 * M2-20), and the customer's invoice page (`/i/{token}`) and its API
 * (`/api/v1/public/invoices/{token}`, M2-16), and the appointment-email page
 * (`/u/{token}`) and its API (`/api/v1/public/appointment-emails/{token}`,
 * M2-21). Anchored to the start of a path — the start of the string, or just
 * after a URL's host — so an `/e/` or `/invite/` further down some other path
 * is left alone.
 */
const LINK_TOKEN_PATH =
  /(^|\/\/[^/\s]+)(\/e\/|\/i\/|\/u\/|\/invite\/|\/api\/v1\/public\/estimates\/|\/api\/v1\/public\/invoices\/|\/api\/v1\/public\/invites\/|\/api\/v1\/public\/appointment-emails\/)[^/?#\s]+/g;

/** The same URL (or bare path, or "GET https://…") with a link token replaced. */
export function redactLinkToken(url: string): string {
  return url.replace(LINK_TOKEN_PATH, `$1$2${REDACTED}`);
}

/**
 * The same URL with every non-allowlisted query value replaced.
 *
 * Parsed by hand rather than through `URL`/`URLSearchParams` because those
 * re-encode as a side effect (`+` becomes `%20`, and so on), which would make
 * the telemetry disagree with the request that was actually sent. A telemetry
 * value that is subtly not the real URL is worse than one that is obviously
 * redacted.
 *
 * Accepts a bare URL or one with a method prefix ("GET https://…"), because
 * that is the shape of a dependency's `name`.
 */
export function redactUrlQuery(url: string): string {
  const space = url.indexOf(' ');
  if (space !== -1) {
    // "GET https://…" — redact the URL half, put the method back.
    return url.slice(0, space + 1) + redactUrlQuery(url.slice(space + 1));
  }

  const questionMark = url.indexOf('?');
  if (questionMark === -1) {
    return url;
  }

  const base = url.slice(0, questionMark + 1);
  let query = url.slice(questionMark + 1);
  let fragment = '';
  const hash = query.indexOf('#');
  if (hash !== -1) {
    fragment = query.slice(hash);
    query = query.slice(0, hash);
  }

  const redacted = query
    .split('&')
    .map((pair) => {
      if (!pair) return pair;
      const equals = pair.indexOf('=');
      // A bare flag (`?debug`) has no value to leak, so it survives as-is.
      if (equals === -1) return pair;
      const rawName = pair.slice(0, equals);
      let name = rawName;
      try {
        name = decodeURIComponent(rawName);
      } catch {
        // A malformed escape is not a reason to fail open — match on the raw
        // name instead, which simply will not be on the allowlist.
      }
      return SAFE_QUERY_PARAMS.has(name) ? pair : `${rawName}=${REDACTED}`;
    })
    .join('&');

  return base + redacted + fragment;
}

/**
 * The App Insights telemetry initializer. Registered by `initTelemetry`.
 *
 * Fields are addressed by telemetry type rather than by sweeping every string
 * on the item, so that a page title or an exception message containing a `?`
 * is not quietly rewritten. The guard against the SDK one day adding another
 * URL-bearing field is not this function — it is `telemetryRedaction.test.ts`,
 * which walks the WHOLE sent item and fails on the search term appearing
 * anywhere in it.
 *
 * Always returns `true`: this filters what an item says, never whether it is
 * sent. Losing telemetry to a bug in here would be a worse outcome than the
 * one it prevents, so it also cannot throw.
 */
export function redactTelemetryUrls(item: ITelemetryItem): boolean {
  try {
    // The operation name is the page's pathname, on every item the page sends.
    const trace = (item.ext as Record<string, unknown> | undefined)?.trace;
    if (trace && typeof trace === 'object') {
      redactField(trace as Record<string, unknown>, 'name');
    }
    const tags = item.tags as Record<string, unknown> | undefined;
    if (tags && typeof tags === 'object' && !Array.isArray(tags)) {
      redactField(tags, 'ai.operation.name');
    }

    const baseData = item.baseData;
    if (!baseData) return true;

    switch (item.baseType) {
      case 'PageviewData':
      case 'PageviewPerformanceData':
        redactField(baseData, 'uri');
        redactField(baseData, 'refUri');
        break;
      case 'RemoteDependencyData':
        // `target` is the absolute URL, `name` is "GET " + that URL, and
        // `data` is the URL again when the SDK populates it. All three.
        redactField(baseData, 'target');
        redactField(baseData, 'name');
        redactField(baseData, 'data');
        break;
      default:
        break;
    }
  } catch {
    // Deliberately swallowed — see above.
  }
  return true;
}

function redactField(baseData: Record<string, unknown>, field: string): void {
  const value = baseData[field];
  if (typeof value === 'string') {
    baseData[field] = redactUrlQuery(redactLinkToken(value));
  }
}
